import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function MyContributions() {
  const navigate = useNavigate();
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) {
      navigate("/signin");
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Fetch donations for the logged-in user
    fetchDonations(userData.email);
  }, [navigate]);

  const fetchDonations = async (email) => {
    setLoading(true);
    try {
      const response = await axios.post("https://api.donatepe.org/db/select", {
        table: "donations",
        columns: [
          "id",
          "fundraiser_id",
          "donor_name",
          "donor_email",
          "donor_mobile",
          "amount",
          "tip_amount",
          "total_amount",
          "created_at"
        ],
        conditions: {
          donor_email: email
        }
      });

      if (response.data && response.data.rows) {
        setDonations(response.data.rows);
      } else {
        setDonations([]);
      }
      setError("");
    } catch (err) {
      console.error("Error fetching donations:", err);
      setError("Failed to load contributions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateTotalContributed = () => {
    return donations.reduce((total, donation) => total + parseFloat(donation.total_amount), 0);
  };

  const getCampaignLink = (fundraiserId) => {
    return `/campaign/${fundraiserId}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">My Contributions</h1>
                <p className="text-gray-500 text-sm">
                  View all your generous contributions
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
            >
              <i className="fas fa-home mr-2"></i>
              Home
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Total Contributions</p>
                <h3 className="text-3xl font-bold mt-2">
                  {formatCurrency(calculateTotalContributed())}
                </h3>
              </div>
              <i className="fas fa-hand-holding-heart text-3xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Donations</p>
                <h3 className="text-3xl font-bold mt-2">{donations.length}</h3>
              </div>
              <i className="fas fa-heart text-3xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Campaigns Supported</p>
                <h3 className="text-3xl font-bold mt-2">
                  {[...new Set(donations.map(d => d.fundraiser_id))].length}
                </h3>
              </div>
              <i className="fas fa-users text-3xl opacity-80"></i>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                Contribution History
              </h2>
              {user && (
                <div className="text-sm text-gray-500">
                  <i className="fas fa-envelope mr-2"></i>
                  {user.email}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mb-4"></div>
              <p className="text-gray-500">Loading your contributions...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
              <p className="text-red-600 font-medium mb-2">{error}</p>
              <button
                onClick={() => user && fetchDonations(user.email)}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition"
              >
                <i className="fas fa-redo mr-2"></i>
                Try Again
              </button>
            </div>
          ) : donations.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-50 mb-4">
                <i className="fas fa-hand-holding-heart text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No Contributions Yet
              </h3>
              <p className="text-gray-500 mb-6">
                You haven't made any contributions yet. Start making a difference today!
              </p>
              <button
                onClick={() => navigate("/campaigns")}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition shadow-lg"
              >
                <i className="fas fa-search mr-2"></i>
                Explore Campaigns
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tip
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {donations.map((donation) => (
                    <tr key={donation.id} className="hover:bg-gray-50 transition">
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-heart text-teal-600"></i>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              Campaign #{donation.fundraiser_id}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {donation.fundraiser_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(donation.amount)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-amber-600 font-medium">
                          {formatCurrency(donation.tip_amount)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm font-bold text-teal-700">
                          {formatCurrency(donation.total_amount)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-500">
                          {formatDate(donation.created_at)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => navigate(getCampaignLink(donation.fundraiser_id))}
                          className="px-4 py-2 text-sm bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition"
                        >
                          <i className="fas fa-external-link-alt mr-1"></i>
                          View Campaign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer Summary */}
          {donations.length > 0 && (
            <div className="bg-gray-50 p-6 border-t">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">
                    Showing {donations.length} contribution{donations.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Grand Total</p>
                  <h3 className="text-2xl font-bold text-teal-700">
                    {formatCurrency(calculateTotalContributed())}
                  </h3>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Download Receipts Section */}
        {donations.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Download Receipts
            </h3>
            <p className="text-gray-600 mb-6">
              All your contributions are eligible for tax deduction under Section 80G.
              Download receipts for your records.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {donations.slice(0, 3).map((donation) => (
                <div key={donation.id} className="border rounded-lg p-4 hover:border-teal-300 transition">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium text-gray-800">
                        Receipt #{donation.id}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {formatDate(donation.created_at)}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      Paid
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-900">
                      {formatCurrency(donation.total_amount)}
                    </span>
                    <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition">
                      <i className="fas fa-download mr-1"></i>
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {donations.length > 3 && (
              <div className="mt-4 text-center">
                <button className="text-teal-600 hover:text-teal-700 font-medium">
                  <i className="fas fa-chevron-down mr-1"></i>
                  Show {donations.length - 3} more receipts
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}